import React from 'react';
import { Award, TrendingUp, Sparkles, CheckCircle2, Zap, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
const DAYS_LEFT = 17;
interface BonusQualificationProps {
  currentPoints: number;
  goalPoints: number;
  unitsClosed: number;
  onClick?: () => void;
}
export function BonusQualification({
  currentPoints,
  goalPoints,
  unitsClosed,
  onClick
}: BonusQualificationProps) {
  const remaining = Math.max(0, goalPoints - currentPoints);
  const percent = Math.min(100, Math.round(currentPoints / goalPoints * 1000) / 10);
  const qualified = currentPoints >= goalPoints;
  const dailyPaceNeeded = Math.ceil(remaining / Math.max(1, DAYS_LEFT));
  const onTrack = dailyPaceNeeded <= 420;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.05
  }} className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md hover:border-blue-300 transition-all' : ''}`} onClick={onClick} data-id="element-37">
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-0" data-id="element-38">
        {/* Left: the main qualification status */}
        <div className="p-5 sm:p-6 border-b lg:border-b-0 lg:border-r border-gray-100" data-id="element-39">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap" data-id="element-40">
            <div className="flex items-center gap-2.5 min-w-0" data-id="element-41">
              <div className="w-9 h-9 rounded-lg bg-yellow-100 flex items-center justify-center shrink-0" data-id="element-42">
                <Award className="w-5 h-5 text-yellow-600" data-id="element-43" />
              </div>
              <div className="min-w-0" data-id="element-44">
                <h2 className="text-base sm:text-lg font-extrabold text-[#0a1e5e] leading-tight" data-id="element-45">
                  Mortgage Depot Scorecard
                </h2>
                <p className="text-xs text-gray-500 leading-tight mt-0.5" data-id="element-46">
                  May 2025 · Monthly Scorecard Goal
                </p>
              </div>
            </div>

            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider shrink-0 ${qualified ? 'bg-green-100 text-green-700' : onTrack ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-47">
              {qualified ? <>
                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-48" /> Qualified
                </> : onTrack ? <>
                  <Zap className="w-3.5 h-3.5" data-id="element-49" /> On Track
                </> : <>
                  <TrendingUp className="w-3.5 h-3.5" data-id="element-50" /> Push Needed
                </>}
            </span>
          </div>

          {/* Big point comparison */}
          <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-5 items-end mb-5" data-id="element-51">
            <div className="min-w-0" data-id="element-52">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-53">
                You Have
              </p>
              <div className="flex items-baseline gap-1.5 flex-wrap" data-id="element-54">
                <span className={`text-4xl sm:text-5xl font-extrabold leading-none tracking-tight ${qualified ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-55">
                  {currentPoints.toLocaleString()}
                </span>
                <span className="text-sm font-bold text-gray-500" data-id="element-56">PTS</span>
              </div>
            </div>
            <div className="hidden sm:flex items-end justify-center pb-2 text-gray-300 text-2xl font-bold" data-id="element-57">
              /
            </div>
            <div className="min-w-0 sm:text-right" data-id="element-58">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-59">
                You Need
              </p>
              <div className="flex sm:justify-end items-baseline gap-1.5 flex-wrap" data-id="element-60">
                <span className="text-4xl sm:text-5xl font-extrabold text-gray-400 leading-none tracking-tight" data-id="element-61">
                  {goalPoints.toLocaleString()}
                </span>
                <span className="text-sm font-bold text-gray-400" data-id="element-62">PTS</span>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4" data-id="element-63">
            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-64">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${percent}%`
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} className={`h-3 rounded-full ${qualified ? 'bg-green-500' : 'bg-blue-600'}`} data-id="element-65" />
            </div>
            <div className="flex justify-between mt-1.5 text-[11px] font-semibold" data-id="element-66">
              <span className={qualified ? 'text-green-600' : 'text-blue-600'} data-id="element-67">
                {percent}% complete
              </span>
              <span className="text-gray-500" data-id="element-68">
                {qualified ? '🎉 Bonus unlocked' : `${remaining.toLocaleString()} pts to go`}
              </span>
            </div>
          </div>

          {/* Daily pace pill */}
          {!qualified && <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2.5" data-id="element-69">
              <Zap className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" data-id="element-70" />
              <div className="min-w-0 text-sm" data-id="element-71">
                <span className="font-bold text-[#0a1e5e]" data-id="element-72">
                  {dailyPaceNeeded} pts/day
                </span>
                <span className="text-gray-700" data-id="element-73">
                  {' '}
                  for the next {DAYS_LEFT} days will get you across the line.
                </span>
              </div>
            </div>}
        </div>

        {/* Right: What you unlock — entire section locked until scorecard goal is met */}
        {qualified ? <div className="bg-gradient-to-br from-yellow-50 to-amber-50/40 p-5 sm:p-6 flex flex-col" data-id="element-74">
            <div className="flex items-center gap-2 mb-3" data-id="element-75">
              <Sparkles className="w-4 h-4 text-yellow-600 shrink-0" data-id="element-76" />
              <h3 className="text-xs font-bold text-yellow-800 uppercase tracking-wider" data-id="element-77">
                What This Unlocks
              </h3>
            </div>
            <p className="text-sm text-gray-700 mb-4 leading-snug" data-id="element-78">
              You hit your monthly goal — you've unlocked{' '}
              <strong className="text-[#0a1e5e]" data-id="element-79">Unit Bonus eligibility</strong>{' '}
              on top of your base comp.
            </p>

            <ul className="space-y-2 text-sm" data-id="element-80">
              <BonusTier label="4+ Units" threshold={4} bonus={5} unitsClosed={unitsClosed} goalMet={qualified} data-id="element-81" />
              <BonusTier label="6+ Units" threshold={6} bonus={20} unitsClosed={unitsClosed} goalMet={qualified} highlight data-id="element-82" />
              <BonusTier label="10+ Units" threshold={10} bonus={30} unitsClosed={unitsClosed} goalMet={qualified} data-id="element-83" />
            </ul>

            <p className="text-[11px] text-gray-500 leading-snug mt-4 pt-3 border-t border-yellow-200/60" data-id="element-84">
              Miss the monthly goal →{' '}
              <strong className="text-red-600" data-id="element-85">no Unit Bonus</strong> for the
              month, even if units are met.
            </p>
          </div> : <div className="relative bg-gray-50 p-5 sm:p-6 flex flex-col" data-id="element-86">
            {/* Greyed-out preview behind the lock */}
            <div aria-hidden="true" className="flex flex-col opacity-75 blur-[0.3px] select-none pointer-events-none" data-id="element-87">
              <div className="flex items-center gap-2 mb-3" data-id="element-88">
                <Sparkles className="w-4 h-4 text-gray-400 shrink-0" data-id="element-89" />
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-90">
                  What This Unlocks
                </h3>
              </div>
              <ul className="space-y-2 text-sm" data-id="element-91">
                {[{
              label: '4+ Units',
              bonus: 5
            }, {
              label: '6+ Units',
              bonus: 20
            }, {
              label: '10+ Units',
              bonus: 30
            }].map(t => <li key={t.label} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 bg-gray-100 border border-gray-200" data-id="element-92">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-93">
                      {t.label}
                    </span>
                    <span className="text-base font-extrabold text-gray-400" data-id="element-94">
                      +{t.bonus} BPS
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Lock overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-95">
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mb-3" data-id="element-96">
                <Lock className="w-6 h-6 text-gray-500" data-id="element-97" />
              </div>
              <p className="text-sm font-bold text-gray-700 leading-snug max-w-[220px]" data-id="element-98">
                Reach your Scorecard Goal to Unlock Bonus Eligibility
              </p>
              <p className="text-xs text-gray-500 mt-2 font-medium" data-id="element-99">
                {remaining.toLocaleString()} pts to go
              </p>
            </div>
          </div>}
      </div>
    </motion.div>;
}
function BonusTier({
  label,
  threshold,
  bonus,
  unitsClosed,
  goalMet,
  highlight
}: {
  label: string;
  threshold: number;
  bonus: number;
  unitsClosed: number;
  goalMet: boolean;
  highlight?: boolean;
}) {
  const unitsMet = unitsClosed >= threshold;
  const unlocked = goalMet && unitsMet;
  const unitsToGo = threshold - unitsClosed;
  return <li className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition-colors ${unlocked ? highlight ? 'bg-white border border-yellow-200 shadow-sm' : '' : 'bg-gray-50 border border-gray-100'}`} data-id="element-100">
      <span className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${unlocked ? 'text-gray-700' : 'text-gray-400'}`} data-id="element-101">
        {!unlocked && <Lock className="w-3 h-3 shrink-0" data-id="element-102" />}
        {label}
      </span>
      <div className="flex items-center gap-2.5 shrink-0" data-id="element-103">
        {!unlocked && <span className="text-[10px] font-semibold text-gray-400" data-id="element-104">
            {!goalMet ? 'Hit point goal' : `${unitsToGo} more unit${unitsToGo === 1 ? '' : 's'}`}
          </span>}
        <div className="flex items-baseline gap-1" data-id="element-105">
          <span className={`text-base font-extrabold ${unlocked ? 'text-green-600' : 'text-gray-400'}`} data-id="element-106">
            +{bonus}
          </span>
          <span className="text-[10px] font-bold text-gray-500 ml-0.5" data-id="element-107">
            BPS
          </span>
        </div>
      </div>
    </li>;
}